import React, { FC, Fragment, useContext, useState } from 'react';
import { DynamicProposal, Proposal } from 'ts/shared/interfaces/CreateProposalParams';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faPencil, faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons';
import { formatPriceCurrency, getCurrency, getLogoUrl } from 'utils/utils';
import { dateFormat, getBounds, formatPriceProposal } from 'utils/shared/utils';
import FreeTextModal from '../Single/FreeTextModal';
import { blobPath } from 'constants/constant';
import { formatFltPersons } from 'utils/shared/proposal';
import withErrorBoundary from '../../withErrorBoundary';
import { MainContext } from 'contexts/MainContext';
import { useProposalUi } from '../Assistant/proposalActions';

interface FlightProposalProps {
  flight: any;
  proposal: Proposal;
  setProposal: (proposal: Proposal) => void;
  proposalIndex: number;
  dynamic?: DynamicProposal;
  openPopup: (product: string, index: number) => void;
  assistantOwner?: boolean;
}
const FlightProposal: FC<FlightProposalProps> = ({ flight, dynamic, assistantOwner = false, ...props }) => {
  const bounds = getBounds(flight) as any[];
  const { t } = useTranslation();
  const [showFreeModal, setShowFreeModal] = useState(false);

  useProposalUi(
    (intent) =>
      !!assistantOwner &&
      intent.kind === 'open-edit' &&
      intent.index === props.proposalIndex &&
      (dynamic
        ? intent.dynamic && intent.product === 'dynamic' && intent.part === 'flight'
        : !intent.dynamic && intent.product === 'flights'),
    (intent) => {
      if (intent.kind === 'open-edit') {
        setShowFreeModal(true);
      }
    },
  );
  const segments = bounds.reduce((acc: any[], bound) => [...acc, ...bound.gdsFlyAirs], []);
  const { cdnUrl } = useContext(MainContext);
  const blobUrl = cdnUrl + blobPath + '/s3/prod.beprotravel/cdn/';
  const bags = dynamic?.bags || props.proposal.flights[props.proposalIndex]?.bags;
  const outbound =
    dynamic?.flight?.outbound || props.proposal.flights[props.proposalIndex]?.item.outbound;
  const flt = props.proposal.flights[props.proposalIndex];
  const isRedHandbag = (bound: any) =>
    !bound?.listOfFlyBaggagePrice ||
    !bound?.listOfFlyBaggagePrice?.[0] ||
    bound?.listOfFlyBaggagePrice?.[0]?.maxWeightHandbag === 0;
  const isGreenTotal = (bound: any) =>
    bound?.listOfFlyBaggagePrice &&
    (bound.listOfFlyBaggagePrice?.[0]?.maxTotalWeight > 0 ||
      bound.listOfFlyBaggagePrice?.[0]?.pieces > 0);

  const onSaveFreeText = (desc: string, displayOrder?: number) => {
    if (dynamic) {
      props.setProposal({
        ...props.proposal,
        dynamic: props.proposal.dynamic.map((d, index) =>
          index === props.proposalIndex ? { ...d, freeTextFlight: desc, displayOrder } : d
        ),
      });
    } else {
      props.setProposal({
        ...props.proposal,
        flights: props.proposal.flights.map((f, i) =>
          i === props.proposalIndex ? { ...f, freeText: desc, displayOrder } : f
        ),
      });
    }
  };

  return (
    <Fragment>
      <tr className="proposal-flight-summary">
        <td colSpan={7} className="pr-data-table-title text-nowrap">
          <div className="display-flex proposal-flight-summary-inner">
            <span className="mr-10 text-bold proposal-flight-summary-provider">
              {flight.flightSource.includes('ALP') ? flight.provider : ''}
            </span>
            <div className="proposal-flight-summary-routes">
              {bounds.map((bound, index) => (
                <span key={index} className="display-flex proposal-flight-summary-leg">
                  <span className="text-contrast display-flex mr-5">
                    {bound.departureAirport.code}{' '}
                    <FontAwesomeIcon icon={faArrowRight} className="mr-5 ml-5" />{' '}
                    {bound.arrivalAirport.code},{' '}
                  </span>
                  <span className="text-bold text-sm display-flex">
                    {dateFormat(bound.departureDateTime, 'dd MMM yy')}
                  </span>

                  {index !== bounds.length - 1 && <span className="ph-10">|</span>}
                </span>
              ))}
            </div>
            <span className="ph-10 proposal-flight-summary-pax">{formatFltPersons(flight, t)}</span>

            {!!bags?.length && (
              <div>
                <span>{t('flight.incl_bag')}: </span>
                <span className="text-bold">
                  {formatPriceCurrency(
                    bags?.reduce((acc, bag) => {
                      const currency = (dynamic?.currency || flt.currency || 'usd').toLowerCase();
                      const price = (bag.displayPrice as any)[currency];

                      return acc + (price || 0);
                    }, 0) || 0,
                    getCurrency(dynamic?.currency || flt.currency)
                  )}
                </span>
              </div>
            )}
            {!isRedHandbag(outbound) && (
              <div className="ph-5">
                <span>{t('flight.incl_handbag')} </span>
                {!!outbound?.listOfFlyBaggagePrice?.[0]?.maxWeightHandbag && (
                  <span>
                    :{' '}
                    {formatPriceCurrency(
                      outbound?.listOfFlyBaggagePrice?.[0]?.maxWeightHandbag,
                      getCurrency(dynamic?.currency || flt.currency)
                    )}
                  </span>
                )}
              </div>
            )}
            {!!isGreenTotal(outbound) && (
              <div className="ph-5">
                <span>{t('flight.incl_suitcase')} </span>
              </div>
            )}
          </div>
        </td>
        <td
          colSpan={2}
          className="pr-data-table-title   text-contrast text-end cur-poi"
          onClick={() => props.openPopup('flights', props.proposalIndex)}
        >
          {!dynamic && (
            <Fragment>
              {!!props.proposal.flights[props.proposalIndex].viewValue && (
                <span>
                  {formatPriceCurrency(
                    props.proposal.flights[props.proposalIndex].price,
                    getCurrency(props.proposal.flights[props.proposalIndex].currency)
                  )}{' '}
                  /{' '}
                </span>
              )}
              <span className="text-lg text-bold">
                {formatPriceProposal(
                  props.proposal.flights[props.proposalIndex].price,
                  props.proposal.flights[props.proposalIndex].currency,
                  props.proposal.flights[props.proposalIndex].viewType,
                  props.proposal.flights[props.proposalIndex].viewValue
                )}
                <FontAwesomeIcon icon={faPencil} className="cur-poi ml-5" size="xs" />
              </span>
            </Fragment>
          )}
        </td>
        <td className="pr-data-table-title">
          <div className="flex-row-end mr-5">
            <FontAwesomeIcon
              icon={faPenToSquare}
              size="lg"
              className="ml-10 cur-poi"
              onClick={() => setShowFreeModal(true)}
            />
            {!dynamic && (
              <FontAwesomeIcon
                icon={faTrash}
                className="ml-10 cur-poi"
                size="lg"
                onClick={() =>
                  props.setProposal({
                    ...props.proposal,
                    flights: props.proposal.flights.filter((f, i) => i !== props.proposalIndex),
                  })
                }
              />
            )}
          </div>
        </td>
      </tr>
      {segments.map((segment, index) => (
        <tr key={index}>
          <td>
            <img src={getLogoUrl(blobUrl, segment.airline.code)} alt="logo" />
          </td>
          <td>
            {segment.airline.code} {segment.flightNumber}
          </td>
          <td>{dateFormat(segment.departureDateTime, 'dd/MM HH:mm')}</td>
          <td>
            <span className="text-bold">{segment.departureAirport.code}</span>
            <span> {segment.departureAirport.cityDesc}</span>
          </td>
          <td>{dateFormat(segment.arrivalDateTime, 'dd/MM HH:mm')}</td>
          <td>
            <span className="text-bold">{segment.arrivalAirport.code}</span>
            <span> {segment.arrivalAirport.cityDesc}</span>
          </td>
          <td>{segment.aircraftType.code}</td>
          <td>{!!segment.elapsedDTime && segment.elapsedDTime}</td>
        </tr>
      ))}
      {showFreeModal && (
        <FreeTextModal
          close={() => setShowFreeModal(false)}
          onSave={onSaveFreeText}
          displayOrder={
            dynamic
              ? dynamic.displayOrder
              : props.proposal.flights[props.proposalIndex].displayOrder
          }
          freeText={
            dynamic
              ? dynamic.freeTextFlight || ''
              : props.proposal.flights[props.proposalIndex]?.freeText || ''
          }
        />
      )}
    </Fragment>
  );
};

export default withErrorBoundary(FlightProposal, 'FlightProposal');
